import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../services/supabase/supabase';
import tournamentService from '../../services/tournamentService';

export const useDisplayData = (connectedTournamentId, t, navigate) => {
  const [tournamentData, setTournamentData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('INIT');
  const debugLogsRef = useRef([]);

  const addLog = useCallback((msg) => {
    const timestamp = new Date().toISOString();
    debugLogsRef.current.push(`[${timestamp}] ${msg}`);
    if (debugLogsRef.current.length > 500) {
      debugLogsRef.current.shift();
    }
  }, []);

  const getDebugLogs = useCallback(() => {
    return debugLogsRef.current;
  }, []);

  const fetchTournamentData = useCallback(async (isInitial = false) => {
    if (!connectedTournamentId) return;
    if (isInitial) {
      setIsLoading(true);
      setError(null);
    }

    // 1. Fetch persistent data (for owner_id, basics)
    const { data: persistentData, error: fetchError } = await tournamentService.getTournamentById(connectedTournamentId);

    if (fetchError || !persistentData) {
      if (isInitial) {
        addLog(`Fetch failure: ${connectedTournamentId}`);
        setError(t('errors.tournament.notFound'));
        setIsLoading(false);
        if (navigate) navigate('/t');
      }
      return;
    }

    addLog(`Persistent data fetched successfully`);

    // 2. Fetch hot data (for latest timer/structure) from tournament_states
    const { data: hotData } = await supabase
      .from('tournament_states')
      .select('*')
      .eq('tournament_id', connectedTournamentId)
      .single();

    addLog(`Hot data fetched. Exists: ${!!hotData}`);

    // 3. Merge: Persistent data first, then overwrite with Hot data if available
    setTournamentData(prev => {
      // If we are re-fetching, we want to update the data but not interrupt local flow if possible.
      return {
        ...persistentData,
        ...(hotData || {}),
        structure: hotData?.structure || persistentData.structure || { columns: [], levels: [], events: [] },
        details: hotData?.details || persistentData.details || {},
        timer_state: hotData?.timer_state || persistentData.timer_state || {},
      };
    });

    if (isInitial) setIsLoading(false);
  }, [connectedTournamentId, navigate, t, addLog]);

  // Initial Fetch & Subscription
  useEffect(() => {
    if (!connectedTournamentId) return;

    let channel;
    let isSubscribed = true;
    let reconnectTimeoutId;
    let proactiveRefreshIntervalId;

    const setupChannel = async (isInitial) => {
      // 古いチャンネルへの参照を一時退避
      const oldChannel = channel;

      // 1. まず新しいチャンネル（パイプ）を繋ぐ（データ漏れを防ぐため）
      addLog(`Setting up new channel... (isInitial: ${isInitial})`);
      const newChannel = tournamentService.subscribeToTournamentState(
        connectedTournamentId,
        (newState) => {
          addLog(`Received realtime update for state`);
          setTournamentData(prevData => ({
            ...prevData,
            name: newState.name || prevData.name,
            structure: newState.structure || prevData.structure,
            details: newState.details || prevData.details,
            timer_state: newState.timer_state || prevData.timer_state,
          }));
        },
        (status, err) => {
          if (!isSubscribed) return;

          // 古いチャンネルからのステータス通知は無視する（自己再接続の無限ループ防止）
          if (channel && channel !== newChannel) return;

          console.log(`[Display] Realtime channel status: ${status}`);
          addLog(`Channel status changed: ${status}`);
          setConnectionStatus(status);
          
          if (status === 'SUBSCRIBED') {
            // 無事つながったら念のため走っていた再接続タイマーをキャンセルする
            if (reconnectTimeoutId) clearTimeout(reconnectTimeoutId);
          } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            console.warn('[Display] Realtime channel lost. Attempting to reconnect in 3000ms...');
            if (reconnectTimeoutId) clearTimeout(reconnectTimeoutId);
            reconnectTimeoutId = setTimeout(() => {
              console.log('[Display] Reconnecting channel...');
              addLog(`Reconnecting channel due to status ${status}`);
              setupChannel(false);
            }, 3000);
          }
        }
      );

      // 新しいチャンネルを現在のチャンネルとしてセット
      channel = newChannel;

      // 2. パイプが繋がった状態で最新データを取得（これで切り替え時の状態変化も上書きで確実に取り込める）
      await fetchTournamentData(isInitial);

      if (!isSubscribed) return;

      // 3. 最後に、安全になったところで古いチャンネルを破棄（オーバーラップさせることで空白期間をゼロにする）
      if (oldChannel) {
        await tournamentService.unsubscribeFromTournamentState(oldChannel);
      }
    };

    setupChannel(true);

    // 予防的パイプ交換（Proactive Channel Refresh）
    // お客様の指摘通り、30分等のキリの良い数字はレベルの境界と衝突する危険があるため、通信断の前に強制交換する
    // 「秒単位での素数」である【539秒（539000ms / 8分59秒）】を採用する（40分強での切断に先回る）
    proactiveRefreshIntervalId = setInterval(() => {
      if (isSubscribed) {
        console.log('[Display] Executing proactive channel refresh (539s interval) to prevent silent drop and level-up collision...');
        addLog(`Executing proactive interval refresh (539000ms)`);
        setupChannel(false);
      }
    }, 539000);

    return () => {
      isSubscribed = false;
      if (reconnectTimeoutId) clearTimeout(reconnectTimeoutId);
      if (proactiveRefreshIntervalId) clearInterval(proactiveRefreshIntervalId);
      if (channel) {
        tournamentService.unsubscribeFromTournamentState(channel);
      }
    };
  }, [connectedTournamentId, fetchTournamentData, addLog]);

  // Re-fetch on visibility change (Sleep/Resume handling)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && connectedTournamentId) {
        console.log('App became visible, re-fetching tournament data...');
        addLog(`App became visible, re-fetching...`);
        fetchTournamentData(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [connectedTournamentId, fetchTournamentData, addLog]);

  return { tournamentData, isLoading, error, fetchTournamentData, connectionStatus, getDebugLogs };
};
